<template>
  <div class="typeahead-container" v-click-outside="closeDropdown">
    <div class="form-floating">
      <input
        type="text"
        class="form-control"
        :id="id"
        :placeholder="placeholder"
        v-model="search"
        @input="onInput"
        @focus="onFocus"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.esc="closeDropdown"
        autocomplete="off"
      />
      <label :for="id">{{ placeholder }}</label>
    </div>

    <ul v-if="isOpen && filteredItems.length > 0" class="typeahead-list shadow">
      <li
        v-for="(item, index) in filteredItems"
        :key="index"
        :class="{ 'active': index === arrowCounter }"
        @click="selectItem(item)"
        @mouseover="arrowCounter = index"
      >
        <slot name="item" :item="item">
           {{ getItemText(item) }}
        </slot>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Buscar...'
  },
  id: {
    type: String,
    default: () => `typeahead-${Math.random().toString(36).substr(2, 9)}`
  },
  itemText: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'selected']);

const search = ref(props.modelValue);
const isOpen = ref(false);
const arrowCounter = ref(-1);

// Normalize string to remove accents and convert to lowercase
const normalize = (str) => {
  if (!str) return '';
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const getItemText = (item) => {
    if (typeof item === 'string') return item;
    if (props.itemText && item[props.itemText]) return item[props.itemText];
    return JSON.stringify(item);
};

const filteredItems = computed(() => {
  if (!search.value) return [];
  const query = normalize(search.value);
  
  return props.items.filter(item => {
      if (typeof item === 'string') {
          return normalize(item).includes(query);
      }
      // If it's an object, search in all its string properties or use itemText
      if (props.itemText && item[props.itemText]) {
          return normalize(item[props.itemText]).includes(query);
      }
      return Object.values(item).some(val => 
          typeof val === 'string' && normalize(val).includes(query)
      );
  });
});

watch(() => props.modelValue, (newVal) => {
  search.value = newVal;
});

const onInput = () => {
  emit('update:modelValue', search.value);
  isOpen.value = true;
  arrowCounter.value = -1;
};

const onFocus = () => {
  if (search.value) {
    isOpen.value = true;
  }
};

const selectItem = (item) => {
  const text = getItemText(item);
  search.value = text;
  isOpen.value = false;
  emit('update:modelValue', text);
  emit('selected', item);
};

const closeDropdown = () => {
  isOpen.value = false;
  arrowCounter.value = -1;
};

const onArrowDown = () => {
  if (arrowCounter.value < filteredItems.value.length - 1) {
    arrowCounter.value++;
  }
};

const onArrowUp = () => {
  if (arrowCounter.value > 0) {
    arrowCounter.value--;
  }
};

const onEnter = () => {
  if (arrowCounter.value !== -1 && filteredItems.value[arrowCounter.value]) {
    selectItem(filteredItems.value[arrowCounter.value]);
  } else if (isOpen.value && filteredItems.value.length > 0) {
    // If no arrow selection but dropdown open, select first item
    selectItem(filteredItems.value[0]);
  } else {
    // If no matches, just close
    isOpen.value = false;
  }
};

// Simple directive for click outside
const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value();
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent);
  }
};
</script>

<style scoped>
.typeahead-container {
  position: relative;
  width: 100%;
}

.typeahead-list {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.5rem 0.5rem;
}

.typeahead-list li {
  padding: 0.75rem 1rem;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s;
  font-size: 0.9rem;
}

.typeahead-list li:hover,
.typeahead-list li.active {
  background-color: #f8f9fa;
  color: var(--primary-alt, #0d6efd);
}

.form-control:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Scrollbar styling to match project */
.typeahead-list::-webkit-scrollbar {
  width: 3px;
}
.typeahead-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.typeahead-list::-webkit-scrollbar-thumb {
  background: var(--primary, #0d6efd);
  border-radius: 10px;
}
</style>
